import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { ClaimLink, FilterSelect, money } from "../components/reports/reportShared.jsx";
import { getOrderWorklist, listEntityRows, listUsers, updateOrder } from "../services/apiClient.js";

// The 1.0 BasicReports.aspx trio as three pages over ONE grid (Robert
// 2026-07-17): hs | contractors | needs_checking (services/basicReports.js).
// Rows edit in place — Comments + Contractor Work Complete save through the
// normal per-order update, so every Orders-tab rule rides along. CSV replaces
// 1.0's Excel export.

const TITLES = {
  hs: "H&S Order Worklist",
  contractors: "Contractors Report",
  needs_checking: "Contractors Complete Needs Checking",
};

const EMPTY = {
  contractor_id: "", division_id: "", job_type_id: "", pco_id: "",
  work_centre_id: "", work_complete: "", invoices: "",
  include_completed: "", exclude_on_hold: "",
};

// Red YES = outstanding (legacy reversed display: the flag stores "uploaded").
const outstanding = (uploaded) =>
  uploaded ? <span className="text-success">No</span> : <span className="text-danger fw-semibold">YES</span>;

function Row({ row, report, onSaved }) {
  const [comments, setComments] = useState(row.comments || "");
  const [complete, setComplete] = useState(!!row.work_complete);
  const [busy, setBusy] = useState(false);
  const dirty = comments !== (row.comments || "") || complete !== !!row.work_complete;

  async function save() {
    setBusy(true);
    try {
      await updateOrder(row.claim_id, row.order_id, { comments, work_complete: complete });
      onSaved(row.order_id, { comments, work_complete: complete });
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <td>{row.order_number}</td>
      <td><ClaimLink claimId={row.claim_id} claimNumber={row.claim_number} tab="orders" /></td>
      <td>{row.client_name}</td>
      <td className="small">{row.street_address}</td>
      <td>{row.pco_name || "-"}</td>
      <td>
        {row.contractor_name || "-"}
        {row.contractor_email ? (
          <a className="ms-1 small" href={`mailto:${row.contractor_email}`}>✉</a>
        ) : null}
      </td>
      <td>{row.work_centre_name || "-"}</td>
      <td className="text-center">{outstanding(row.risk_assessment_uploaded)}</td>
      <td className="text-center">{outstanding(row.photos_uploaded)}</td>
      <td className="text-end">{money(row.invoices_received)}</td>
      <td className="text-center">
        <Form.Check type="checkbox" checked={complete} onChange={(e) => setComplete(e.target.checked)}
          disabled={report === "hs"} data-hint={report === "hs" ? "Ticked on the Contractors reports" : "Contractor work complete"} />
      </td>
      <td style={{ minWidth: 180 }}>
        <Form.Control size="sm" value={comments} onChange={(e) => setComments(e.target.value)} />
      </td>
      <td>
        <Button size="sm" variant={dirty ? "primary" : "outline-secondary"} disabled={!dirty || busy} onClick={save}>
          Save
        </Button>
      </td>
    </tr>
  );
}

function OrderWorklistPage({ report }) {
  const [filters, setFilters] = useState(EMPTY);
  const [opts, setOpts] = useState({});
  const [pcos, setPcos] = useState([]);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const title = TITLES[report];

  useEffect(() => {
    Promise.all(
      ["contractor-companies", "divisions", "job-types", "work-centres"].map((k) =>
        listEntityRows(k).then((r) => [k, r])
      )
    ).then((pairs) => setOpts(Object.fromEntries(pairs))).catch(() => {});
    listUsers({ pco: true, activeOnly: true }).then(setPcos).catch(() => {});
  }, []);

  async function search(next = filters) {
    setError("");
    try {
      const params = Object.fromEntries(Object.entries(next).filter(([, v]) => v !== ""));
      const res = await getOrderWorklist({ report, ...params });
      setRows(res.rows);
    } catch (err) {
      setError(err.response?.data?.error || "Search failed.");
    }
  }

  useEffect(() => { search(EMPTY); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [report]);

  // FilterSelect hands over the VALUE; the two plain Form.Selects below hand
  // over the EVENT. This took either from the start... except it did not: it
  // read e.target.value inside the state updater, so picking a Contractor,
  // Division, Job Type, PCO or Work Centre threw during React's next render
  // and, with no boundary then, blanked the page (KS 2026-09-08). Any pick
  // from those five was broken; the two Form.Selects and Search were fine.
  const set = (k) => (e) => {
    const value = e && typeof e === "object" && "target" in e ? e.target.value : e;
    setFilters((f) => ({ ...f, [k]: value ?? "" }));
  };
  const setBool = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.checked ? "1" : "" }));

  function exportCsv() {
    if (!rows?.length) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["Order #", "Job #", "Client", "Address", "PCO", "Contractor", "Email", "Work Centre", "HS Outstanding", "Photos Outstanding", "Invoices Received", "Work Complete", "Comments"];
    const lines = rows.map((r) => [
      r.order_number, r.claim_number, r.client_name, r.street_address, r.pco_name,
      r.contractor_name, r.contractor_email, r.work_centre_name,
      r.risk_assessment_uploaded ? "No" : "YES", r.photos_uploaded ? "No" : "YES",
      r.invoices_received, r.work_complete ? "Yes" : "No", r.comments,
    ].map(esc).join(","));
    const blob = new Blob([[head.map(esc).join(","), ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <section className="page-panel">
      <div className="page-heading"><h2>{title} <RulesHelp topic="orderWorklists" /></h2></div>

      <div className="d-flex flex-wrap align-items-end mb-2 no-print">
        <FilterSelect label="Contractor" value={filters.contractor_id} onChange={set("contractor_id")} options={opts["contractor-companies"] || []} width={200} />
        <FilterSelect label="Division" value={filters.division_id} onChange={set("division_id")} options={opts.divisions || []} width={140} />
        <FilterSelect label="Job Type" value={filters.job_type_id} onChange={set("job_type_id")} options={opts["job-types"] || []} width={170} />
        <FilterSelect label="Project Co-Ordinator" value={filters.pco_id} onChange={set("pco_id")} options={pcos} width={170} labelField="full_name" />
        <FilterSelect label="Work Centre" value={filters.work_centre_id} onChange={set("work_centre_id")} options={opts["work-centres"] || []} width={160} />
        {report === "contractors" ? (
          <>
            <Form.Group className="me-3 mb-1">
              <Form.Label className="mb-0 small fw-semibold">Work complete</Form.Label>
              <ThemedSelect size="sm" style={{ width: 100 }} value={filters.work_complete} onChange={set("work_complete")}>
                <option value="">All</option><option value="yes">Yes</option><option value="no">No</option>
              </ThemedSelect>
            </Form.Group>
            <Form.Group className="me-3 mb-1">
              <Form.Label className="mb-0 small fw-semibold">Invoices</Form.Label>
              <ThemedSelect size="sm" style={{ width: 110 }} value={filters.invoices} onChange={set("invoices")}>
                <option value="">All</option><option value="received">Received</option><option value="none">None</option>
              </ThemedSelect>
            </Form.Group>
          </>
        ) : null}
        <Form.Check className="me-3 mb-2" type="checkbox" id="owl-completed" label="Include recently completed"
          checked={filters.include_completed === "1"} onChange={setBool("include_completed")} />
        <Form.Check className="me-3 mb-2" type="checkbox" id="owl-onhold" label="Exclude on hold"
          checked={filters.exclude_on_hold === "1"} onChange={setBool("exclude_on_hold")} />
        <Button size="sm" className="mb-2 me-2" onClick={() => search()}>Search</Button>
        <Button size="sm" variant="outline-secondary" className="mb-2 me-2"
          onClick={() => { setFilters(EMPTY); search(EMPTY); }}>Clear</Button>
        <Button size="sm" variant="outline-success" className="mb-2" onClick={exportCsv} disabled={!rows?.length}>CSV</Button>
      </div>

      {error ? <Alert variant="danger" className="py-2">{error}</Alert> : null}

      {rows ? (
        <Table striped bordered hover responsive size="sm" className="align-middle">
          <thead className="table-surface">
            <tr>
              <th>Order #</th><th>Job #</th><th>Client</th><th>Address</th><th>PCO</th>
              <th>Contractor</th><th>Work Centre</th>
              <th className="text-center">H&S outstanding</th><th className="text-center">Photos outstanding</th>
              <th className="text-end">Invoices received</th><th className="text-center">Work complete</th>
              <th>Comments</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Row key={r.order_id} row={r} report={report}
                onSaved={(id, patch) => setRows((rs) => rs.map((x) => (x.order_id === id ? { ...x, ...patch } : x)))} />
            ))}
            {rows.length === 0 ? <tr><td colSpan={13} className="text-center text-muted">Nothing matches.</td></tr> : null}
          </tbody>
        </Table>
      ) : null}
      {rows?.length >= 250 ? <div className="text-muted small">Showing the first 250 — narrow the filters.</div> : null}
    </section>
  );
}

export default OrderWorklistPage;
